// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 zouwei

/**
 * Markdown → board elements (the inverse of scene.ts `extractMarkdown`).
 *
 * Turns a Markdown document's block structure into placeable board
 * elements: `$$…$$` blocks become display formulas, standalone `$…$` blocks
 * become inline formulas, everything else becomes text. Blocks are stacked
 * vertically; the host decides final placement (elements start at a given
 * origin and flow downward).
 *
 * This is deliberately self-contained (no @moraya/core ProseMirror parse):
 * the block grammar we need is small, and staying dependency-free keeps the
 * conversion usable in any host without pulling the editor schema. The
 * @moraya/core peer stays optional for richer future use.
 */

import type { BoardElement, FormulaElement, TextElement } from './types'

const DEFAULT_FONT_SIZE = 20
const BLOCK_GAP = 24
const ORIGIN = { x: 40, y: 40 }

function newId(prefix: string, index: number): string {
  // Deterministic-ish, collision-free within one conversion; hosts may
  // reassign ids on insert if they need globally unique values.
  return `${prefix}-${index}-${Math.round(index * 2654435761) % 100000}`
}

function classifyBlock(
  block: string,
): { kind: 'display' | 'inline'; latex: string } | { kind: 'text'; text: string } {
  const t = block.trim()
  if (t.length >= 4 && t.startsWith('$$') && t.endsWith('$$')) {
    return { kind: 'display', latex: t.slice(2, -2).trim() }
  }
  // Standalone inline formula: a block that is exactly $...$ with no inner $.
  if (
    t.length >= 2 &&
    t.startsWith('$') &&
    t.endsWith('$') &&
    !t.slice(1, -1).includes('$')
  ) {
    return { kind: 'inline', latex: t.slice(1, -1).trim() }
  }
  return { kind: 'text', text: block.replace(/\s+$/, '') }
}

/** Split into blocks on blank lines (one or more empty lines). */
function splitBlocks(md: string): string[] {
  return md
    .split(/\n[ \t]*\n/)
    .map((b) => b.trim())
    .filter((b) => b.length > 0)
}

function estHeight(block: { kind: string }, fontSize: number): number {
  return block.kind === 'display' ? fontSize * 2 : fontSize * 1.6
}

/**
 * Convert Markdown to a vertically-stacked list of text/formula elements.
 * Round-trips with `extractMarkdown`: extractMarkdown(elementsFromMarkdown(md))
 * reproduces the normalized block sequence.
 */
export function elementsFromMarkdown(
  md: string,
  opts: { fontSize?: number; origin?: { x: number; y: number } } = {},
): BoardElement[] {
  const fontSize = opts.fontSize ?? DEFAULT_FONT_SIZE
  const origin = opts.origin ?? ORIGIN
  let y = origin.y
  const out: BoardElement[] = []

  splitBlocks(md).forEach((block, i) => {
    const c = classifyBlock(block)
    if (c.kind === 'text') {
      const el: TextElement = {
        id: newId('text', i),
        type: 'text',
        x: origin.x,
        y,
        text: c.text,
        fontSize,
      }
      out.push(el)
    } else {
      const el: FormulaElement = {
        id: newId('formula', i),
        type: 'formula',
        x: origin.x,
        y,
        latex: c.latex,
        display: c.kind === 'display',
        fontSize,
      }
      out.push(el)
    }
    y += estHeight(c, fontSize) + BLOCK_GAP
  })

  return out
}

/** Only the formula elements from a Markdown document. */
export function formulasFromMarkdown(
  md: string,
  opts?: { fontSize?: number; origin?: { x: number; y: number } },
): FormulaElement[] {
  return elementsFromMarkdown(md, opts).filter(
    (el): el is FormulaElement => el.type === 'formula',
  )
}
